import React, { useMemo, useState, useEffect, useCallback } from 'react';
import { Bracket, IRoundProps, Seed, SeedItem, SeedTeam, IRenderSeedProps } from 'react-brackets';
import { BracketPreviewModel } from '../../utils/apiClient';

interface BracketsPreviewProps {
  data: BracketPreviewModel;
  onApprove?: (rounds: { roundNumber: number; matches: { homeParticipantId?: number | null; awayParticipantId?: number | null }[] }[]) => void;
}

const getStageLabel = (matchesCount: number) => {
  if (matchesCount <= 1) return 'Финал';
  const power = Math.round(Math.log2(Math.max(1, matchesCount)));
  const denom = 2 ** power;
  return `1/${denom} финала`;
};

const mapToRounds = (data: BracketPreviewModel): IRoundProps[] =>
  (data.rounds ?? []).map((round, rIdx) => ({
    title: getStageLabel((round.matches ?? []).length),
    seeds: (round.matches ?? []).map((m, i) => {
      const teams: { id?: number; name?: string }[] = [];
      teams.push({ id: (m as any).homeParticipantId ?? undefined, name: m.homeParticipantName || 'TBD' } as any);
      teams.push({ id: (m as any).awayParticipantId ?? undefined, name: m.awayParticipantName || 'TBD' } as any);
      return {
        id: `${rIdx}-${i}`,
        teams,
      } as any;
    }),
  }));

const BracketsPreview: React.FC<BracketsPreviewProps> = ({ data, onApprove }) => {
  const initialRounds = useMemo<IRoundProps[]>(() => mapToRounds(data), [data]);
  const [roundsState, setRoundsState] = useState<IRoundProps[]>(initialRounds);
  const [selected, setSelected] = useState<{
    roundIndex: number; seedIndex: number; teamIndex: number;
  } | null>(null);

  useEffect(() => {
    setRoundsState(mapToRounds(data));
    setSelected(null);
  }, [data]);

  useEffect(() => {
    // eslint-disable-next-line no-console
    console.log('Bracket preview rounds (local state):', roundsState);
  }, [roundsState]);

  const recalcNextRoundByes = useCallback((prev: IRoundProps[], baseRoundIndex: number): IRoundProps[] => {
    // Only propagate byes to the immediate next round
    if (baseRoundIndex < 0 || baseRoundIndex >= prev.length - 1) return prev;
    const current = prev[baseRoundIndex];
    const next = prev[baseRoundIndex + 1];

    const updatedNextSeeds = next.seeds.map((seed, k) => {
      const newSeed: any = { ...seed, teams: [ ...(seed as any).teams ] };
      const srcA: any = current.seeds[2 * k];
      const srcB: any = current.seeds[2 * k + 1];
      const teamsA = (srcA?.teams ?? []) as Array<{ id?: number; name?: string }>;
      const teamsB = (srcB?.teams ?? []) as Array<{ id?: number; name?: string }>;
      const presentA = teamsA.filter(t => t && t.id !== undefined);
      const presentB = teamsB.filter(t => t && t.id !== undefined);

      const TBD = { id: undefined, name: 'TBD' } as any;
      newSeed.teams[0] = (presentA.length === 1) ? presentA[0] : TBD;
      newSeed.teams[1] = (presentB.length === 1) ? presentB[0] : TBD;
      return newSeed;
    });

    return prev.map((r, idx) => idx === baseRoundIndex + 1 ? ({ ...r, seeds: updatedNextSeeds }) : r);
  }, []);

  const onTeamClick = useCallback((roundIndex: number, seedIndex: number, teamIndex: number) => {
    if (!selected) {
      setSelected({ roundIndex, seedIndex, teamIndex });
      return;
    }
    const a = selected;
    const b = { roundIndex, seedIndex, teamIndex };
    if (a.roundIndex === b.roundIndex && a.seedIndex === b.seedIndex && a.teamIndex === b.teamIndex) {
      setSelected(null);
      return;
    }
    setRoundsState(prev => {
      // Deep copy seeds and teams for immutability
      let next = prev.map(r => ({ ...r, seeds: r.seeds.map(s => ({ ...s, teams: [...(s as any).teams] })) }));

      const teamA = (next[a.roundIndex].seeds[a.seedIndex] as any).teams[a.teamIndex];
      const teamB = (next[b.roundIndex].seeds[b.seedIndex] as any).teams[b.teamIndex];
      (next[a.roundIndex].seeds[a.seedIndex] as any).teams[a.teamIndex] = teamB;
      (next[b.roundIndex].seeds[b.seedIndex] as any).teams[b.teamIndex] = teamA;

      // Recalculate byes for the immediate next round(s) impacted by the swap
      const impacted = new Set<number>([a.roundIndex, b.roundIndex]);
      impacted.forEach((ri) => {
        next = recalcNextRoundByes(next, ri);
      });

      return next;
    });
    setSelected(null);
  }, [selected, recalcNextRoundByes]);

  const CustomSeed = useCallback(({ seed, breakpoint, roundIndex, seedIndex }: IRenderSeedProps) => {
    const teams = (seed as any).teams as Array<{ id?: number; name?: string }>;
    const isTopSelected = !!(selected && selected.roundIndex === roundIndex && selected.seedIndex === seedIndex && selected.teamIndex === 0);
    const isBottomSelected = !!(selected && selected.roundIndex === roundIndex && selected.seedIndex === seedIndex && selected.teamIndex === 1);

    const common: React.CSSProperties = {
      cursor: 'pointer',
      color: '#ffffff',
      borderRadius: 6,
      padding: '4px 8px',
      border: 'none'
    };

    const topStyle: React.CSSProperties = isTopSelected
      ? { background: '#ffffff', color: '#000000', border: '2px solid #000000' }
      : { background: '#1d4ed8', color: '#ffffff' }; // blue-700

    const bottomStyle: React.CSSProperties = isBottomSelected
      ? { background: '#ffffff', color: '#000000', border: '2px solid #000000' }
      : { background: '#1d4ed8', color: '#ffffff' }; // blue-700 for all

    return (
      <Seed mobileBreakpoint={breakpoint}>
        <SeedItem>
          <div>
            <SeedTeam
              onClick={() => onTeamClick(roundIndex!, seedIndex!, 0)}
              style={{ ...common, ...topStyle }}
            >
              {teams[0]?.name || 'TBD'}
            </SeedTeam>
            <SeedTeam
              onClick={() => onTeamClick(roundIndex!, seedIndex!, 1)}
              style={{ ...common, ...bottomStyle, marginTop: 6 }}
            >
              {teams[1]?.name || 'TBD'}
            </SeedTeam>
          </div>
        </SeedItem>
      </Seed>
    );
  }, [onTeamClick, selected]);

  const handleApprove = useCallback(() => {
    if (!onApprove) return;
    const payload = (roundsState as any[]).map((r: any, idx: number) => ({
      roundNumber: r.roundNumber || idx + 1,
      matches: (r.seeds as any[]).map((s: any) => ({
        homeParticipantId: s.teams[0]?.id ?? null,
        awayParticipantId: s.teams[1]?.id ?? null,
      }))
    }));
    onApprove(payload);
  }, [onApprove, roundsState]);

  return (
    <div style={{ overflow: 'auto' }}>
      <div style={{ marginBottom: 8, color: '#6b7280' }}>
        Режим замены: кликните по двум участникам, чтобы поменять их местами. (Предпросмотр, без сохранения)
      </div>
      <Bracket
        rounds={roundsState}
        mobileBreakpoint={0}
        roundTitleComponent={(title) => (
          <div style={{ textAlign: 'center' }}>{title}</div>
        )}
        renderSeedComponent={CustomSeed}
      />
      {onApprove && (
        <div style={{ marginTop: 12 }}>
          <button onClick={handleApprove} className="btn btn--register">
            Утвердить сетку
          </button>
        </div>
      )}
    </div>
  );
};

export default BracketsPreview; 